import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { Subject, Subscription } from 'rxjs';

/**
 * This service handles image loading and management for camera feeds
 */
@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private imageSub: Subscription;
  private timeout: any;

  private imageLoadingSource = new Subject<void>();
  private imageLoadedSource = new Subject<void>();

  /** Event emitted when an image has finished loading */
  imageLoadedEvent = this.imageLoadedSource.asObservable();
  /** Event emitted when an image starts loading */
  imageLoadingEvent = this.imageLoadingSource.asObservable();

  constructor(private http: HttpClient, private domSanitizer: DomSanitizer) { }

  /**
   * Get current image from a camera
   * @param  {number} cameraId - Camera ID
   * @returns {Promise<SafeUrl>} Promise that resolves to a SafeUrl of the image
   */
  getImage(cameraId: number): Promise<SafeUrl> {
    return new Promise((resolve, reject) => {
      this.imageLoadingSource.next();
      if (cameraId) {
        let observable = this.http.get<Blob>(
          `${environment.imageApi}/Image/${cameraId}`,
          { responseType: 'blob' as 'json', headers: { Accept: 'image/jpeg' } }
        );

        if (this.imageSub) this.disposeSubscription();

        this.imageSub = observable.subscribe({
          next: (blob) => {
            const img = this.domSanitizer.bypassSecurityTrustUrl(
              URL.createObjectURL(blob)
            );
            resolve(img);
          },
          error: (err) => {
            reject(err);
          },
          complete: () => {
            this.imageLoadedSource.next();
            this.disposeSubscription();
          }
        });

        this.timeout = setTimeout(() => {
          this.disposeSubscription();
          this.imageLoadedSource.next();
          reject('timeout');
        }, environment.imageTimeout);
      } else {
        this.imageLoadedSource.next();
        reject();
      }
    });
  }

  /**
   * Get recording snapshot from a camera at a specific time
   * @param  {number} cameraId - Camera ID
   * @param  {Date} time - Time for the snapshot
   * @returns {Promise<SafeUrl>} Promise that resolves to a SafeUrl of the snapshot
   */
  getRecordingSnapshot(cameraId: number, time: Date): Promise<SafeUrl> {
    return new Promise((resolve, reject) => {
      this.imageLoadingSource.next();
      if (cameraId) {
        let observable = this.http.get<Blob>(
          `${environment.imageApi}/Image/${cameraId}/Recording?time=${time.toISOString()}`,
          { responseType: 'blob' as 'json', headers: { Accept: 'image/jpeg' } }
        );

        if (this.imageSub) this.disposeSubscription();

        this.imageSub = observable.subscribe({
          next: (blob) => {
            const img = this.domSanitizer.bypassSecurityTrustUrl(
              URL.createObjectURL(blob)
            );
            resolve(img);
          },
          error: (err) => {
            reject(err);
          },
          complete: () => {
            this.imageLoadedSource.next();
            this.disposeSubscription();
          }
        });

        this.timeout = setTimeout(() => {
          this.disposeSubscription();
          this.imageLoadedSource.next();
          reject('timeout');
        }, environment.imageTimeout);
      } else {
        this.imageLoadedSource.next();
        reject();
      }
    });

  }

  /**
   * Dispose of the current image subscription
   */
  private disposeSubscription() {
    clearTimeout(this.timeout);
    this.imageSub.unsubscribe();
    this.imageSub = null;
  }
}
